import validator from 'validator';
import React from 'react';
import { navigate } from '@reach/router';
import isEqual from 'lodash.isequal';
import * as userHelpers from '../helpers/User';
import * as firebaseHelpers from '../helpers/Firebase/';
import * as apiHelpers from '../helpers/API';
import { isValidPassword } from '../helpers/Validators';
import PaymentMethodInput from '../components/PaymentMethodInput';
import DeactivateAccount from '../components/Dashboard/Organization/Settings/Desktop/DeactivateAccount';
import ChangePassword from '../components/ChangePassword';
import { setLoading } from './loaderActions';
import * as types from './actionTypes';
import { loadTransactions, loadBalance } from './transactionsActions';
import { loadDeposits } from './depositsActions';
import { loadSubscriptions } from './subscriptionsActions';
import { hideModal, showModal } from './modalActions';
import { loadUsers } from './usersActions';
import {
  loadBillingDetails, loadBillingHistory, updateBillingMethod,
} from './billingActions';
import { setGivingOrganization, getOrganizations } from './dashboardActions';
const securePageMatch = /(dashboard)|(on-board\/document)|(on-board\/retry)|(on-board#(2|3|4|5|6))/;

export function signOut() {
  return async dispatch => {
    dispatch(setLoading(true));
    await firebaseHelpers.signOut();
    dispatch(clearUserData());
    await navigate('/login');
    dispatch(setLoading(false));
  };
}

export function setUserExists(email) {
  return async dispatch => {
    const userExists = await apiHelpers.checkIfUserExists(email);

    dispatch({
      type: types.SET_USER_EXISTS,
      userExists,
    });
  };
}

export function createAccount(formData, accountType = 0, stopLoadingOnComplete = true) {
  return async dispatch => {
    let user;

    try {
      dispatch({ type: types.LOGGING_IN, loggingIn: true });
      dispatch(setLoading(true));
      dispatch(clearUserData());
      user = await userHelpers.createAccount(formData, accountType);

      if (user) {
        dispatch(setAccountType(accountType));
        await dispatch(getAccountData(user));
        dispatch(loadUserData(user));
      }
    } catch (e) {
      console.log(e);
      dispatch(setLoading(false));
      dispatch({ type: types.GOT_USER, user: null });
      alert(e.message || e);
    }
    dispatch({ type: types.LOGGING_IN, loggingIn: false });

    if (stopLoadingOnComplete) {
      dispatch(setLoading(false));
    }
    return user;
  };
}

export function login(formData, redirect) {
  return async dispatch => {
    try {
      redirect = redirect || '/dashboard';
      dispatch({ type: types.LOGGING_IN, loggingIn: true });
      dispatch(setLoading(true));
      dispatch(clearUserData());

      const user = await userHelpers.login(formData);

      if (user) {
        dispatch(getAccountType());
        await dispatch(getAccountData(user));
        dispatch(loadUserData(user));
        await navigate(redirect);
        dispatch(setLoading(false));
      } else {
        dispatch(setLoading(false));
      }
    } catch (e) {
      console.log(e);
      dispatch(setLoading(false));
      dispatch({ type: types.GOT_USER, user: null });
      alert(e.message || e);
    }
    dispatch(setLoading(false));
    dispatch({ type: types.LOGGING_IN, loggingIn: false });
  };
}

export function checkLoginForm({
  email, password, passwordConfirm, agreeToTOS,
}, skipPasswordConfirm = false) {
  return async dispatch => {
    if (!agreeToTOS) {
      alert('You must accept to Terms Of Service & Privacy Policy before creating an account');
      return false;
    }

    if (!email || !validator.isEmail(email)) {
      alert('Email is invalid, please use a valid email address');
      return false;
    }

    if (!isValidPassword(password)) {
      alert(
        'Password is invalid, must contain the following: \n- One uppercase letter \n- One lowercase letter\n- One special character:(!@#$%^&*)\n- Greater than 7 characters'
      );
      return false;
    }

    if (!skipPasswordConfirm && passwordConfirm !== password) {
      alert('Password fields must match');
      return false;
    }
    dispatch(setLoading(true));
    const userExists = await apiHelpers.checkIfUserExists(email);
    dispatch(setLoading(false));

    if (userExists) {
      alert('The email associated with this user already exists.\nPlease use another one or login.');
      return false;
    }
    return true;
  };
}

export function getAccountData(user) {
  return async (dispatch, getState) => {
    const { accountType } = getState().userReducer;
    const path = accountType === 1 ? `users/${user.uid}` : `organizations/${user.uid}`;
    const accountData = await firebaseHelpers.getDocument(path);

    dispatch({
      type: types.SET_ACCOUNT_DATA,
      accountData,
    });
  };
}

export function getAccountType() {
  return async (dispatch) => {
    const accountType = await userHelpers.getAccountType();
    dispatch(setAccountType(accountType));
  };
}

export function watchFirebaseAuthState() {
  return (dispatch) => {
    dispatch({ type: types.LOGGING_IN, loggingIn: true });
    firebaseHelpers.authStateChanged(async user => {
      dispatch({ type: types.GOT_USER, user });

      await dispatch(getOrganizations());

      if ((!user || (user && user.isAnonymous)) && window.location.href.match(securePageMatch)) {
        await navigate('/login');
      } else if (user && !user.isAnonymous) {
        await dispatch(getAccountType());
        await dispatch(loadUserData(user));
      }
      dispatch({ type: types.LOGGING_IN, loggingIn: false });

      if (!window.location.pathname) {
        await navigate('/');
      }
    });
  };
}

export function clearUserData() {
  return dispatch => {
    dispatch(setAccountType(null));
    dispatch({
      type: types.SET_ACCOUNT_DATA,
      accountData: {},
    });
    dispatch({
      type: types.LOAD_USERS,
      users: [],
    });
    dispatch({
      type: types.SET_BALANCE,
      merchantBalance: 0,
    });
    dispatch({
      type: types.LOAD_DEPOSITS,
      deposits: [],
    });
    dispatch({
      type: types.LOAD_BILLING_DETAILS,
      billingDetails: {},
    });
    dispatch({
      type: types.LOAD_BILLING_HISTORY,
      billingHistory: [],
    });
    dispatch({
      type: types.LOAD_TRANSACTIONS,
      transactions: [],
    });
  };
}

export function setOrganizationDisplayName(organizationId = '') {
  return async dispatch => {
    let organizationDisplayName = '';

    if (organizationId) {
      const merchantData = await apiHelpers.getOrganizationData(organizationId);
      organizationDisplayName = merchantData ? merchantData.details.displayName : '';
    }
    dispatch({
      type: types.SET_ORG_DISPLAY_NAME,
      organizationDisplayName: organizationDisplayName || '',
    });
  };
}

export function setAccountType(accountType) {
  return {
    type: types.SET_ACCOUNT_TYPE,
    accountType,
  };
}

export function loadUserData(user = {}) {
  return async (dispatch, getState) => {
    const { accountType } = getState().userReducer;

    if (accountType === 1) {
      await dispatch(loadUserState(user));
    } else if (accountType === 0) {
      await dispatch(loadOrganizationState(user));
    }
  };
}


export function loadUserState(user) {
  // eslint-disable-next-line arrow-body-style
  return (dispatch, getState) => {
    return new Promise((resolve) => {
      firebaseHelpers.watchDocumentChanges(`users/${user.uid}`, async accountData => {
        const { accountType } = getState().userReducer;
        const { selectedGivingOrganization } = getState().dashboardReducer;
        const { processor_types } = selectedGivingOrganization || {};

        if (!accountType === 1) {
          return;
        }

        const { accountData: _accountData } = getState().userReducer;

        if (processor_types) {
          accountData.cards = userHelpers.removeUnusableCards(accountData.cards, processor_types);
        }

        if (!isEqual(accountData, _accountData)) {
          const organizations = accountData.organizations ? accountData.organizations : [];
          await dispatch(setOrganizationDisplayName(organizations[0]));

          if (!selectedGivingOrganization && organizations[0]) {
            await dispatch(setGivingOrganization(organizations[0]));
          }
          await dispatch(loadTransactions());
          const { selectedGivingOrganization } = getState().dashboardReducer;
          const { processor_types } = selectedGivingOrganization || {};

          if (processor_types) {
            accountData.cards = userHelpers.removeUnusableCards(accountData.cards, processor_types);
          }
          await dispatch({
            type: types.SET_ACCOUNT_DATA,
            accountData,
          });
          await dispatch(loadSubscriptions());
          resolve();
        } else {
          console.log('User data already loaded');
          resolve();
        }
      });
    });
  };
}

export function loadOrganizationState(user) {
  // eslint-disable-next-line arrow-body-style
  return (dispatch, getState) => {
    return new Promise((resolve) => {
      firebaseHelpers.watchDocumentChanges(
        `organizations/${user.uid}`,
        accountData => {
          const { accountType } = getState().userReducer;

          if (!accountType === 0) {
            return;
          }
          dispatch(setGivingOrganization(accountData.details.id));

          const { accountData: _accountData } = getState().userReducer;

          if (!isEqual(accountData, _accountData)) {
            dispatch({
              type: types.SET_ACCOUNT_DATA,
              accountData,
            });
          } else {
            console.log('User data already loaded');
          }
          resolve();
          updateApplicationStatus();
          dispatch(loadUsers());
          dispatch(loadBalance());
          dispatch(loadDeposits());
          dispatch(loadBillingDetails());
          dispatch(loadBillingHistory());
          dispatch(loadTransactions());
        });
    });
  };
}

export function updateApplicationStatus() {
  return async (_, getState) => {
    try {
      const {
        signature_status,
        boarding_status,
      } = getState().userReducer.accountData;

      if (signature_status !== 'SIGNED' || boarding_status !== 'BOARDED') {
        await apiHelpers.updateApplicationStatus();
      }
    } catch (e) {
      console.warn(e);
    }
  };
}

export function addPaymentMethod(formData) {
  return async (dispatch, getState) => {
    try {
      if ((formData.paymentType === 'cc' && formData.ccToken) || (formData.paymentType === 'ach' && formData.achToken)) {
        const { selectedGivingOrganization } = getState().dashboardReducer;
        dispatch(setLoading(true));
        await apiHelpers.addPaymentMethod(formData, selectedGivingOrganization.id);
        dispatch(setLoading(false));
        return true;
      } else {
        return alert('Could not read payment details please input again.');
      }
    } catch (e) {
      alert(e.message || e);
      dispatch(setLoading(false));
      return false;
    }
  };
}

export function updateSubscriptionMethodWithModal(subscriptionId) {
  return (dispatch, getState) => new Promise((resolve, reject) => {
    try {
      const { selectedGivingOrganization } = getState().dashboardReducer;
      const { processor_types } = selectedGivingOrganization;
      const { accountData = {} } = getState().userReducer;

      dispatch(showModal(<PaymentMethodInput
        {...accountData.profile}
        processorTypes={processor_types}
        submit={async (payload) => {
          if (payload) {
            dispatch(setLoading(true));
            await dispatch(addPaymentMethod(payload));
            await apiHelpers.updateSubscriptionMethod(payload.last4, payload.processorType, subscriptionId);
            resolve();
          }
        }} />, { fullscreen: true }));
    } catch (e) {
      alert(e.message || e);
      dispatch(hideModal());
      dispatch(setLoading(false));
      reject(e);
    }
  });
}

export function updateSubscriptionStatus(subscriptionId, status) {
  return async dispatch => {
    try {
      dispatch(setLoading(true));
      await apiHelpers.updateSubscriptionStatus(subscriptionId, status);
      await dispatch(loadSubscriptions());
    } catch (e) {
      alert(e.message || e);
    }
    dispatch(setLoading(false));
    return true;
  };
}

export function changePasswordWithModal() {
  return dispatch => {
    dispatch(showModal(<ChangePassword changePassword={(oldPassword, password, passwordConfirm) => {
      dispatch(changePassword(oldPassword, password, passwordConfirm));
    }} />));
  };
}

export function changePassword(oldPassword, password, passwordConfirm) {
  return async (dispatch, getState) => {
    try {
      if (password !== passwordConfirm) {
        throw new Error('New passwords must match.');
      } else if (!isValidPassword(password)) {
        throw new Error(
          'Password is invalid, must contain the following: \n- One uppercase letter \n- One lowercase letter\n- One special character:(!@#$%^&*)\n- Greater than 7 characters'
        );
      }
      dispatch(setLoading(true));
      await firebaseHelpers.reauthenticate(oldPassword);
      const { accountType } = getState().userReducer;
      await apiHelpers.changePassword(password, accountType);
      await dispatch(signOut());
    } catch (e) {
      alert(e.message);
    }
    dispatch(setLoading(false));
    return true;
  };
}

export function saveUserDetails(formData) {
  return async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      const { accountType } = getState().userReducer;
      await apiHelpers.updateUserAccount(formData, accountType);
    } catch (e) {
      alert(e.message || e);
    }
    dispatch(setLoading(false));
    return true;
  };
}

export function replaceMerchantBillingMethodWithModal(monthlySubscription) {
  return (dispatch) => {
    const updateSubscriptionMethod = parseInt(monthlySubscription) !== 0;

    dispatch(showModal(
      <PaymentMethodInput
        submit={(data) => {
          if (data) {
            dispatch(updateBillingMethod(data));
          }
        }}
        title="Replace payment method"
        processorTypes={updateSubscriptionMethod ? undefined : { ach: 'legacy' }}
      />));
  };
}

export function deactivateAccount() {
  return dispatch => {
    dispatch(showModal(
      <DeactivateAccount
        hideModal={() => dispatch(hideModal())}
        deactivateOrganization={() => dispatch(deactivateOrganization())}
      />));
  };
}

export function deactivateOrganization() {
  return async dispatch => {
    try {
      dispatch(hideModal());
      dispatch(setLoading(true));
      await apiHelpers.deactivateOrganization();
      setTimeout(() => {
        navigate('/login');
        dispatch(setLoading(false));
      }, 500);
    } catch (e) {
      console.log(e);
      alert('There was a problem deactivating your account');
      dispatch(setLoading(false));
    }
  };
}

export function updateGivingOptions(givingOptions) {
  return async dispatch => {
    try {
      dispatch(hideModal());
      dispatch(setLoading(true));
      const newOptions = await apiHelpers.updateGivingOptions(givingOptions);

      dispatch({
        type: types.UPDATE_GIVING_OPTIONS,
        givingOptions: newOptions,
      });
    } catch (e) {
      console.log(e);
      alert('There was a problem adding your giving option');
    }
    dispatch(setLoading(false));
  };
}

export function resendApplicationEmail() {
  return async dispatch => {
    try {
      dispatch(setLoading(true));
      await apiHelpers.sendApplicationSignatureEmail();
    } catch (e) {
      console.error(e);
      alert('There was a problem sending your email application');
    }
    dispatch(setLoading(false));
  };
}

export function updateFormDetails(color, logo) {
  return async dispatch => {
    try {
      dispatch(hideModal());
      await apiHelpers.updateForm({ color, logo });
    } catch (e) {
      console.log(e);
      alert('There was a problem adding your new color, please try again.');
    }
  };
}

export function deletePaymentMethods(cards = []) {
  return async dispatch => {
    try {
      dispatch(setLoading(true));
      await apiHelpers.deletePaymentMethods(cards);
    } catch (e) {
      console.error(e);
      alert(e.message || e);
    }
    dispatch(setLoading(false));
  };
}
